import { z } from 'zod';

// Common validation schemas (DRY principle)
const projectNameValidation = z
  .string()
  .min(3, { message: "Project name must be at least 3 characters" })
  .max(50, { message: "Project name must be less than 50 characters" })
  .regex(/^[\w\s.,!?-]+$/, {
    message: "Project name can only contain letters, numbers, spaces, and basic punctuation (.,!?-_)"
  })
  .trim();

const projectSlugValidation = z
  .string()
  .min(1, { message: "Project slug is required" })
  .max(60, { message: "Project slug must be less than 60 characters" })
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
    message: "Slug must contain only lowercase letters, numbers, and hyphens"
  });

const projectOrderValidation = z
  .number()
  .int()
  .min(0, { message: "Order must be a positive number" });

/**
 * Project Creation Schema
 * Used for validating new project creation data
 */
export const ProjectCreateSchema = z.object({
  name: projectNameValidation,
  slug: projectSlugValidation.optional(), // Optional because it can be auto-generated from name
});

/**
 * Project Update Schema
 * Used for validating project updates
 */
export const ProjectUpdateSchema = z.object({
  name: projectNameValidation.optional(),
  slug: projectSlugValidation.optional(),
  order: projectOrderValidation.optional(),
});

/**
 * Project Reorder Schema
 * Used for validating project reordering
 */
export const ProjectReorderSchema = z.object({
  projectIds: z
    .array(z.string())
    .min(1, { message: "At least one project ID required" })
    .max(100, { message: "Maximum 100 projects allowed" }),
});

/**
 * Project Schema
 * Full project model type
 */
export const ProjectSchema = z.object({
  id: z.string(),
  uuid: z.string(),
  name: z.string(),
  slug: z.string(),
  order: z.number().int(),
  userId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

/**
 * Project with Canvas Count Schema
 * Project with additional canvas count information
 */
export const ProjectWithCountSchema = ProjectSchema.extend({
  canvasCount: z.number().int().min(0),
});

/**
 * Project Query Params Schema
 * Used for filtering and sorting projects
 */
export const ProjectQuerySchema = z.object({
  sortBy: z
    .enum(['name', 'order', 'created', 'updated'])
    .default('order'),

  sortOrder: z
    .enum(['asc', 'desc'])
    .default('asc'),
});

// TypeScript types inferred from schemas
export type ProjectCreateInput = z.infer<typeof ProjectCreateSchema>;
export type ProjectUpdateInput = z.infer<typeof ProjectUpdateSchema>;
export type ProjectReorderInput = z.infer<typeof ProjectReorderSchema>;
export type Project = z.infer<typeof ProjectSchema>;
export type ProjectWithCount = z.infer<typeof ProjectWithCountSchema>;
export type ProjectQuery = z.infer<typeof ProjectQuerySchema>;

// Database model type (extended from base schema)
export type ProjectModel = Project & {
  canvases?: any[]; // Will be populated when needed
};